import { supabase } from '@/lib/supabase';
import type { Json } from '@/types/database.types';
import { migrateProfileDoc, PROFILE_DOC_VERSION, type ProfileDoc } from '../model/profileDoc';

/**
 * Profile Studio persistence: one draft row + one published row per user.
 * Drafts are private (owner RLS); published docs are world-readable because
 * they style public profile surfaces. Publishing copies draft → published;
 * reverting copies published → draft; resetting deletes both.
 */

export async function getProfileDraft(userId: string): Promise<ProfileDoc | null> {
  const { data, error } = await supabase
    .from('profile_studio_drafts')
    .select('doc')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return data ? migrateProfileDoc(data.doc) : null;
}

export async function saveProfileDraft(userId: string, doc: ProfileDoc): Promise<void> {
  const { error } = await supabase.from('profile_studio_drafts').upsert({
    user_id: userId,
    doc: doc as unknown as Json,
    doc_version: PROFILE_DOC_VERSION,
  });
  if (error) throw error;
}

export async function getPublishedProfileDoc(userId: string): Promise<ProfileDoc | null> {
  const { data, error } = await supabase
    .from('profile_studio_published')
    .select('doc')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return data ? migrateProfileDoc(data.doc) : null;
}

/** Publish the current draft — the public profile updates immediately. */
export async function publishProfileDoc(userId: string, doc: ProfileDoc): Promise<void> {
  await saveProfileDraft(userId, doc);
  const { error } = await supabase.from('profile_studio_published').upsert({
    user_id: userId,
    doc: doc as unknown as Json,
    doc_version: PROFILE_DOC_VERSION,
    published_at: new Date().toISOString(),
  });
  if (error) throw error;
}

/** Discard unpublished edits — draft returns to the published state (or default). */
export async function revertProfileDraft(userId: string): Promise<ProfileDoc | null> {
  const published = await getPublishedProfileDoc(userId);
  if (published) {
    await saveProfileDraft(userId, published);
    return published;
  }
  const { error } = await supabase.from('profile_studio_drafts').delete().eq('user_id', userId);
  if (error) throw error;
  return null;
}

/** Full reset — removes the customization everywhere (surfaces fall back to defaults). */
export async function resetProfileStudio(userId: string): Promise<void> {
  const drafts = await supabase.from('profile_studio_drafts').delete().eq('user_id', userId);
  if (drafts.error) throw drafts.error;
  const published = await supabase.from('profile_studio_published').delete().eq('user_id', userId);
  if (published.error) throw published.error;
}

export async function listMyAssets(ownerId: string) {
  const { data, error } = await supabase
    .from('theme_assets')
    .select('*')
    .eq('owner_id', ownerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function deleteAsset(assetId: string, path: string): Promise<void> {
  const storage = await supabase.storage.from('theme-assets').remove([path]);
  if (storage.error) throw storage.error;
  const { error } = await supabase.from('theme_assets').delete().eq('id', assetId);
  if (error) throw error;
}
